// Touch helpers for press-and-hold, mostly about iOS.
//
// A tick: `navigator.vibrate` where it exists (Android); iOS has no vibrate,
// but Safari 18 plays the system haptic when a switch-style checkbox toggles,
// so a hidden one is toggled instead. Either way it is best effort and silent
// where neither works.
//
// The click after a hold: when a hold opens something under the finger (a
// sheet), iOS sends the click that ends the touch to whatever is under the
// finger by then, usually the sheet's backdrop, which closes it at once. After
// a hold, the next click is eaten.

let haptic: HTMLLabelElement | null = null

export const tick = (ms = 10) => {
  try {
    if (typeof navigator.vibrate === 'function' && navigator.vibrate(ms)) return
    if (!haptic) {
      haptic = document.createElement('label')
      haptic.setAttribute('aria-hidden', 'true')
      haptic.style.display = 'none'
      const input = document.createElement('input')
      input.type = 'checkbox'
      input.setAttribute('switch', '')
      input.tabIndex = -1
      haptic.appendChild(input)
      document.body.appendChild(haptic)
    }
    haptic.click()
  } catch {
    /* no haptics here */
  }
}

/**
 * Eat the click that ends the current touch. Armed when a hold fires (the
 * finger is still down); the click follows the finger lifting, so the window
 * opens then and closes shortly after, whatever happens.
 */
export const swallowNextClick = () => {
  const eat = (e: Event) => {
    e.preventDefault()
    e.stopPropagation()
    done()
  }
  let timer = 0
  const done = () => {
    window.removeEventListener('click', eat, true)
    window.removeEventListener('pointerup', lifted, true)
    window.removeEventListener('pointercancel', lifted, true)
    window.clearTimeout(timer)
  }
  const lifted = () => {
    window.removeEventListener('pointerup', lifted, true)
    window.removeEventListener('pointercancel', lifted, true)
    window.addEventListener('click', eat, true)
    timer = window.setTimeout(done, 450)
  }
  window.addEventListener('pointerup', lifted, true)
  window.addEventListener('pointercancel', lifted, true)
  // a hold that never lifts (the page went away) must not leave a trap behind
  timer = window.setTimeout(done, 10_000)
}

/**
 * While a finger is holding something, nothing on the page may be selected.
 * iOS starts its own text selection partway through a long press, and by then
 * the hold has opened a sheet under the finger, so making the icon alone
 * unselectable is not enough: the whole document goes unselectable (the
 * `holding` class on <html>) and `selectstart` is refused until the finger
 * lifts, and anything that slipped through is cleared. Returns the release.
 */
let holds = 0
const refuse = (e: Event) => e.preventDefault()
const clearSelection = () => {
  try {
    window.getSelection()?.removeAllRanges()
  } catch {
    /* nothing selected */
  }
}
export const guardSelection = () => {
  if (holds++ === 0) {
    document.documentElement.classList.add('holding')
    document.addEventListener('selectstart', refuse, true)
  }
  let released = false
  const release = () => {
    if (released) return
    released = true
    window.removeEventListener('pointerup', release, true)
    window.removeEventListener('pointercancel', release, true)
    // iOS may still be finishing its long press as the finger lifts: let go a moment later
    window.setTimeout(() => {
      clearSelection()
      if (--holds === 0) {
        document.documentElement.classList.remove('holding')
        document.removeEventListener('selectstart', refuse, true)
      }
    }, 120)
  }
  window.addEventListener('pointerup', release, true)
  window.addEventListener('pointercancel', release, true)
  // a touch whose end never arrives (the app went to the background) must not leave the page unselectable
  window.setTimeout(release, 15_000)
  return release
}
